import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { articlesApi } from '@/api/articles'
import { vocabularyApi } from '@/api/vocabulary'
import { settingsApi } from '@/api/settings'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { pickRandom } from '@/lib/pickRandomWords'
import { maxArticleUnits } from '@/config/practiceLimits'
import { mergeWordAndPhraseRows, toPracticeUnitRef } from '@/lib/mergeVocabularyUnits'
import { AI_CREDIT_COSTS } from '@/lib/aiCredits'
import { useAiCredits } from '@/hooks/useAiCredits'
import { useAiJobPoller, isPersonalizedArticleResult } from '@/hooks/useAiJobPoller'
import { AiCreditsIndicator, AiCreditCostBadge, aiCreditsExceededMessage } from '@/components/ai/AiCreditsIndicator'
import { useSettingsStore } from '@/store/settingsStore'
import { CEFR_LEVEL_OPTIONS } from '@/types/settings'
import type { CefrLevel } from '@/types/common'
import { cn } from '@/components/ui/cn'

interface GeneratePersonalizedArticlePanelProps {
  embedded?: boolean
}

const DURATION_OPTIONS = [3, 5, 7, 10] as const

export function GeneratePersonalizedArticlePanel({
  embedded = false,
}: GeneratePersonalizedArticlePanelProps) {
  const { reading } = useMinCopy()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { recordCharge, usage } = useAiCredits()
  const settingsFromStore = useSettingsStore((state) => state.settings)
  const isPremium = usage?.tier === 'PRO' || usage?.tier === 'ADMIN'
  const [jobId, setJobId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [shuffleKey, setShuffleKey] = useState(0)
  const [cefrLevel, setCefrLevel] = useState<CefrLevel>('B1')
  const [estimatedTime, setEstimatedTime] = useState<number>(5)
  const [selectedTopics, setSelectedTopics] = useState<string[]>([])

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.getSettings,
    staleTime: 60_000,
    enabled: !settingsFromStore,
  })

  const defaultCefrLevel = settingsFromStore?.targetEnglishLevel
    ?? settingsQuery.data?.targetEnglishLevel
    ?? 'B1'

  const preferredTags = settingsFromStore?.preferredTags
    ?? settingsQuery.data?.preferredTags
    ?? []

  const articleTagsQuery = useQuery({
    queryKey: ['articles', 'tags'],
    queryFn: () => articlesApi.getTags().then((response) => response.data),
    staleTime: 60_000,
    enabled: isPremium,
  })

  const topicOptions = preferredTags.length > 0
    ? preferredTags
    : (articleTagsQuery.data ?? [])

  useEffect(() => {
    setCefrLevel(defaultCefrLevel)
  }, [defaultCefrLevel])

  const wordsQuery = useQuery({
    queryKey: ['vocabulary', 'personalized-article-words'],
    queryFn: () =>
      vocabularyApi
        .getWords({ page: 0, size: 100 })
        .then((response) => response.data.content),
    staleTime: 60_000,
  })

  const phrasesQuery = useQuery({
    queryKey: ['vocabularyPhrases', 'personalized-article'],
    queryFn: () =>
      vocabularyApi.getPhrases({ page: 0, size: 100 }).then((response) => response.data.content),
    staleTime: 60_000,
  })

  const vocabularyPool = useMemo(
    () => mergeWordAndPhraseRows(wordsQuery.data ?? [], phrasesQuery.data ?? []),
    [wordsQuery.data, phrasesQuery.data],
  )

  const selectedWords = useMemo(() => {
    return pickRandom(vocabularyPool, maxArticleUnits)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vocabularyPool, shuffleKey])

  const generateMutation = useMutation({
    mutationFn: (payload: {
      units: ReturnType<typeof toPracticeUnitRef>[]
      cefrLevel: CefrLevel
      estimatedTime: number
      tags?: string[]
    }) => articlesApi.generatePersonalized(payload),
    onSuccess: async (response) => {
      setError(null)
      await recordCharge(AI_CREDIT_COSTS.articleGenerate)
      setJobId(response.data.jobId)
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string }; status?: number } })?.response?.data
          ?.message
      const status = (err as { response?: { status?: number } })?.response?.status
      setError(
        status === 429 ? aiCreditsExceededMessage(message) : message ?? reading.generatePersonalizedFailed
      )
    },
  })

  useAiJobPoller({
    jobId,
    onStatus: () => setError(null),
    onDone: async (response) => {
      setJobId(null)
      if (isPersonalizedArticleResult(response.result)) {
        await queryClient.invalidateQueries({ queryKey: ['articles'] })
        navigate(`/reading/${response.result.articleId}?generated=1`)
        return
      }
      setError(reading.generatePersonalizedFailed)
    },
    onError: (message) => {
      setJobId(null)
      setError(message)
    },
  })

  const units = selectedWords.map(toPracticeUnitRef)
  const isGenerating = generateMutation.isPending || jobId !== null
  const hasWords = units.length > 0

  const toggleTopic = (tag: string) => {
    setSelectedTopics((current) => {
      if (current.includes(tag)) {
        return current.filter((item) => item !== tag)
      }
      if (current.length >= 2) {
        return current
      }
      return [...current, tag]
    })
  }

  const content = (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="personalized-cefr" className="text-xs text-muted-foreground">
            {reading.generatePersonalizedFieldLevel}
          </Label>
          <select
            id="personalized-cefr"
            value={cefrLevel}
            onChange={(event) => setCefrLevel(event.target.value as CefrLevel)}
            disabled={isGenerating}
            className="flex h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm"
            data-testid="generate-personalized-cefr"
          >
            {CEFR_LEVEL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.value}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="personalized-duration" className="text-xs text-muted-foreground">
            {reading.generatePersonalizedFieldDuration}
          </Label>
          <select
            id="personalized-duration"
            value={estimatedTime}
            onChange={(event) => setEstimatedTime(Number(event.target.value))}
            disabled={isGenerating}
            className="flex h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm"
            data-testid="generate-personalized-duration"
          >
            {DURATION_OPTIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {formatMessage(reading.durationMinutes, { minutes })}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isPremium && topicOptions.length > 0 ? (
        <div className="mb-4 space-y-2">
          <Label className="text-xs text-muted-foreground">
            {reading.generatePersonalizedFieldTopics}
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {topicOptions.map((tag) => (
              <button
                key={tag}
                type="button"
                disabled={isGenerating}
                onClick={() => toggleTopic(tag)}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize transition-colors',
                  selectedTopics.includes(tag)
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/40'
                )}
                data-testid={`generate-personalized-topic-${tag}`}
              >
                {tag}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{reading.generatePersonalizedTopicsHint}</p>
        </div>
      ) : null}

      {hasWords ? (
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-muted-foreground">
              {(selectedWords.length < maxArticleUnits
                ? formatMessage(reading.generatePersonalizedWordCount, { count: selectedWords.length, max: maxArticleUnits })
                : formatMessage(reading.generatePersonalizedWordCountMax, { max: maxArticleUnits }))}
            </p>
            {selectedWords.length >= maxArticleUnits ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
                onClick={() => setShuffleKey((k) => k + 1)}
                disabled={isGenerating}
              >
                <RefreshCw className="h-3 w-3" />
                {reading.shuffleWords}
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selectedWords.map((word) => (
              <span
                key={word.id}
                className="rounded-full border border-violet-500/20 bg-violet-500/5 px-2.5 py-0.5 text-xs font-medium text-foreground"
              >
                {word.word}
              </span>
            ))}
          </div>
        </div>
      ) : wordsQuery.isSuccess && phrasesQuery.isSuccess && vocabularyPool.length === 0 ? (
        <p className="mb-4 text-sm text-muted-foreground">{reading.generatePersonalizedNoWords}</p>
      ) : null}

      {error ? <p className="mb-3 text-sm text-destructive">{error}</p> : null}

      <AiCreditsIndicator compact className="mb-3" />

      <Button
        className={cn('w-full', !embedded && 'sm:w-auto')}
        disabled={isGenerating || !hasWords}
        onClick={() =>
          generateMutation.mutate({
            units,
            cefrLevel,
            estimatedTime,
            ...(isPremium && selectedTopics.length > 0 ? { tags: selectedTopics } : {}),
          })
        }
        data-testid="generate-personalized-article-start"
      >
        {isGenerating ? (
          <span className="inline-flex items-center gap-2">
            <LoadingSpinner size="sm" />
            {reading.generatePersonalizedLoading}
          </span>
        ) : (
          <>
            {reading.generatePersonalizedCta}
            <AiCreditCostBadge cost={AI_CREDIT_COSTS.articleGenerate} className="ml-1 bg-primary-foreground/15 text-primary-foreground" />
          </>
        )}
      </Button>
    </>
  )

  if (embedded) {
    return <div data-testid="generate-personalized-article">{content}</div>
  }

  return (
    <div
      className="mb-8 rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/5 to-card p-5"
      data-testid="generate-personalized-article"
    >
      {content}
    </div>
  )
}
